import { Component, Input, OnInit, OnDestroy, ElementRef, ViewChild, AfterViewInit, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';

import * as echarts from 'echarts';

export interface RadarChartData {
  radar?: {
    radius?: string;
    center?: readonly [string, string] | [string, string];
    splitNumber?: number;
    splitArea?: any;
    indicator: Array<{ name: string; max: number }>;
  };
  legend?: {
    left?: string;
    bottom?: string;
    data: string[];
  };
  series: Array<{
    name: string;
    value: number[];
    color?: string;
    areaStyle?: any;
  }>;
}

@Component({
  selector: 'app-radar-chart',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './radar-chart.component.html',
  styleUrls: ['./radar-chart.component.css']
})
export class RadarChartComponent implements OnInit, OnDestroy, AfterViewInit, OnChanges {
  @Input() className: string = 'chart';
  @Input() width: string = '100%';
  @Input() height: string = '300px';
  @Input() chartData: RadarChartData | null = null;
  @Input() autoResize: boolean = true;

  @ViewChild('chartContainer', { static: true }) chartContainer!: ElementRef;

  private chart: echarts.ECharts | null = null;
  private animationDuration = 3000;

  ngOnInit(): void {
    // Component initialization
  }

  ngAfterViewInit(): void {
    this.initChart();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['chartData'] && this.chart) {
      this.updateChart();
    }
  }

  ngOnDestroy(): void {
    if (this.chart) {
      this.chart.dispose();
      this.chart = null;
    }
  }

  private initChart(): void {
    if (!this.chartContainer?.nativeElement) return;

    this.chart = echarts.init(this.chartContainer.nativeElement);
    this.updateChart();

    // Handle window resize
    if (this.autoResize) {
      window.addEventListener('resize', () => {
        if (this.chart) {
          this.chart.resize();
        }
      });
    }
  }

  private updateChart(): void {
    if (!this.chart) return;

    const defaultData: RadarChartData = {
      radar: {
        radius: '66%',
        center: ['50%', '42%'],
        splitNumber: 8,
        splitArea: {
          areaStyle: {
            color: 'rgba(127,95,132,.3)',
            opacity: 1,
            shadowBlur: 45,
            shadowColor: 'rgba(0,0,0,.5)',
            shadowOffsetX: 0,
            shadowOffsetY: 15
          }
        },
        indicator: [
          { name: 'Sales', max: 10000 },
          { name: 'Administration', max: 20000 },
          { name: 'Information Technology', max: 20000 },
          { name: 'Customer Support', max: 20000 },
          { name: 'Development', max: 20000 },
          { name: 'Marketing', max: 20000 }
        ]
      },
      legend: {
        left: 'center',
        bottom: '10',
        data: ['Allocated Budget', 'Expected Spending', 'Actual Spending']
      },
      series: [
        {
          name: 'Allocated Budget',
          value: [5000, 7000, 12000, 11000, 15000, 14000]
        },
        {
          name: 'Expected Spending',
          value: [4000, 9000, 15000, 15000, 13000, 11000]
        },
        {
          name: 'Actual Spending',
          value: [5500, 11000, 12000, 15000, 12000, 12000]
        }
      ]
    };

    const data = this.chartData || defaultData;

    this.chart.setOption({
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'shadow'
        }
      },
      radar: {
        radius: data.radar?.radius || '66%',
        center: data.radar?.center || ['50%', '42%'],
        splitNumber: data.radar?.splitNumber || 8,
        splitArea: data.radar?.splitArea || {
          areaStyle: {
            color: 'rgba(127,95,132,.3)',
            opacity: 1,
            shadowBlur: 45,
            shadowColor: 'rgba(0,0,0,.5)',
            shadowOffsetX: 0,
            shadowOffsetY: 15
          }
        },
        indicator: data.radar?.indicator || [
          { name: 'Sales', max: 10000 },
          { name: 'Administration', max: 20000 },
          { name: 'Information Technology', max: 20000 },
          { name: 'Customer Support', max: 20000 },
          { name: 'Development', max: 20000 },
          { name: 'Marketing', max: 20000 }
        ]
      },
      legend: {
        left: data.legend?.left || 'center',
        bottom: data.legend?.bottom || '10',
        data: data.legend?.data || ['Allocated Budget', 'Expected Spending', 'Actual Spending']
      },
      series: data.series.map(item => ({
        type: 'radar',
        name: item.name,
        symbolSize: 0,
        areaStyle: item.areaStyle || {
          normal: {
            shadowBlur: 13,
            shadowColor: 'rgba(0,0,0,.2)',
            shadowOffsetX: 0,
            shadowOffsetY: 10,
            opacity: 1
          }
        },
        data: [{
          value: item.value,
          name: item.name
        }],
        animationDuration: this.animationDuration
      }))
    });
  }

  public updateChartData(data: RadarChartData): void {
    this.chartData = data;
    if (this.chart) {
      this.updateChart();
    }
  }

  public resize(): void {
    if (this.chart) {
      this.chart.resize();
    }
  }
}
